import { css } from '@styled-system/css';
import type { FC, ReactNode } from 'react';

export interface BreadcrumbItem {
  title: string;
  /** Rend le crumb cliquable (navigation retour). */
  onClick?: () => void;
}

export interface PageHeaderProps {
  title: string;
  /** Leading slot before the title block (thumbnail, icon…). */
  leading?: ReactNode;
  /** Optional small mono eyebrow above the title (e.g. "AUTOCOP / FEED"). */
  eyebrow?: string;
  /** Legacy breadcrumbs — rendered as a thin secondary row when present. */
  breadcrumbs?: BreadcrumbItem[];
  /** Inline extras rendered on the title row (chips, external link…). */
  titleExtras?: ReactNode;
  /** Inline stats next to the title (mono font, separated by a divider). */
  stats?: ReactNode;
  /** Centered slot between the title block and the actions (e.g. global search). */
  center?: ReactNode;
  /** Right-aligned action slot — typically buttons, search, etc. */
  actions?: ReactNode;
}

/**
 * PageHeader — single-row header inside the main content card.
 * Layout: [title (with optional eyebrow / breadcrumbs)] [flex spacer] [actions]
 */
export const PageHeader: FC<PageHeaderProps> = ({ title, leading, eyebrow, breadcrumbs, titleExtras, stats, center, actions }) => {
  const showBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '6',
        paddingX: '12',
        minHeight: '56px',
      })}
    >
      {leading}
      <div className={css({ display: 'flex', flexDirection: 'column', gap: '1', minWidth: 0 })}>
        {eyebrow && (
          <div
            className={css({
              fontSize: 'micro',
              fontFamily: 'mono',
              color: 'text.tertiary',
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              fontWeight: 'medium',
            })}
          >
            {eyebrow}
          </div>
        )}
        {showBreadcrumbs && !eyebrow && (
          <nav
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '2',
              fontSize: 'caption',
              color: 'text.tertiary',
            })}
            aria-label="Breadcrumb"
          >
            {breadcrumbs!.map((item, index) => {
              const isLast = index === breadcrumbs!.length - 1;
              return (
                <span key={`${item.title}-${index}`} className={css({ display: 'inline-flex', alignItems: 'center', gap: '2' })}>
                  {item.onClick ? (
                    <button
                      type="button"
                      onClick={item.onClick}
                      className={css({
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        fontSize: 'caption',
                        fontFamily: 'sans',
                        color: 'text.tertiary',
                        cursor: 'pointer',
                        _hover: { color: 'text.primary', textDecoration: 'underline' },
                      })}
                    >
                      {item.title}
                    </button>
                  ) : (
                    <span style={{ color: isLast ? 'var(--colors-text-secondary)' : undefined }}>{item.title}</span>
                  )}
                  {!isLast && <span className={css({ color: 'text.tertiary' })}>/</span>}
                </span>
              );
            })}
          </nav>
        )}
        <div className={css({ display: 'flex', alignItems: 'center', gap: '3', minWidth: 0 })}>
          <h1
            className={css({
              fontSize: 'h2',
              fontWeight: 'semibold',
              lineHeight: 'h2',
              color: 'text.primary',
              letterSpacing: '-0.3px',
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            })}
          >
            {title}
          </h1>
          {titleExtras}
        </div>
      </div>
      {stats && (
        <div className={css({ display: 'flex', alignItems: 'center', gap: '5', flexShrink: 0 })}>
          <span className={css({ width: '1px', height: '20px', bg: 'border.default' })} />
          <div
            className={css({
              fontSize: 'caption',
              fontFamily: 'mono',
              color: 'text.tertiary',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3',
            })}
          >
            {stats}
          </div>
        </div>
      )}
      <div className={css({ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0, paddingX: '6' })}>
        {center}
      </div>
      {actions && (
        <div className={css({ display: 'flex', alignItems: 'center', gap: '3', flexShrink: 0 })}>
          {actions}
        </div>
      )}
    </div>
  );
};
